// Decide a qué pantalla de calibración va un instrumento según su magnitud.
// Es el punto único de ramificación usado por la ficha y el panel técnico.

function matchesType(type, ...needles) {
  const text = String(type || '').toLowerCase();
  return needles.some((needle) => text.includes(String(needle).toLowerCase()));
}

const ELECTRICAL_UNITS = ['v', 'mv', 'kv', 'a', 'ma', 'µa', 'ua', 'ka', 'ω', 'ohm', 'ohms', 'hz', 'khz', 'mhz'];
const PRESSURE_UNITS = ['bar', 'mbar', 'psi', 'pa', 'kpa', 'mpa'];
const TEMPERATURE_UNITS = ['°c', 'ºc', 'c', '°f', 'ºf', 'f', 'k'];
const FLOW_UNITS = ['kg/min', 'kg/h', 't/h', 'g/min', 'm3/h', 'm³/h', 'l/min', 'l/h', 'l/s', 'nl/h'];
const MASS_UNITS = ['kg', 'g', 'gr', 'mg', 't', 'tn', 'lb', 'lbs'];
const FORCE_UNITS = ['n', 'kn', 'kgf', 'gf', 'lbf', 'nm', 'n·m', 'n.m', 'kgf.cm', 'kgf·cm', 'kgf.m', 'kgf·m', 'lbf·ft', 'lb-ft', 'lbf·in', 'lb-in'];
const GAS_UNITS = ['ppm', '%vol', '% vol', '%lel', '% lel'];
const DIMENSIONAL_UNITS = ['mm', 'cm', 'm', 'um', 'µm', 'in', 'inch', '°', 'deg'];
const ENVIRONMENTAL_UNITS = ['m/s', 'm/seg', 'km/h', 'ft/min', 'fpm', 'lx', 'lux', 'klx', 'db', 'dba', 'dbc'];

function isAnalyticalInstrument(instrument = {}) {
  const type = String(instrument?.type || '').toLowerCase();
  return matchesType(
    type, 'phmetro', 'ph metro', 'medidor de ph', 'conductiv', 'turbid',
    'oxímetro', 'oximetro', 'oxígeno disuelto', 'oxigeno disuelto',
    'refract', 'brix', 'alcoholímetro', 'alcoholimetro', 'densímetro', 'densimetro',
    'mostímetro', 'mostimetro', 'baumé', 'baume', 'bureta', 'micropipeta', 'pipeta',
    'material volumétrico', 'material volumetrico',
  );
}

function isMassFlowInstrument(instrument = {}) {
  const type = String(instrument?.type || '').toLowerCase();
  const unit = String(instrument?.unit || '').toLowerCase();
  return matchesType(type, 'coriolis', 'masic', 'másic', 'mass flow')
    || ['kg/min', 'kg/h', 't/h', 'g/min'].includes(unit);
}

function isMassWeightInstrument(instrument = {}) {
  const type = String(instrument?.type || '').toLowerCase();
  return matchesType(type, 'masa patrón', 'masa patron', 'pesa patrón', 'pesa patron', 'peso patrón', 'peso patron', 'test weight', 'standard weight')
    || matchesType(type, 'pesa calibrada', 'juego de pesas');
}

function isBalanceInstrument(instrument = {}) {
  const type = String(instrument?.type || '').toLowerCase();
  const unit = String(instrument?.unit || '').toLowerCase();
  return matchesType(type, 'balanza', 'báscula', 'bascula', 'weighing scale', 'weigh scale', 'balance')
    || (MASS_UNITS.includes(unit)
      && !isMassWeightInstrument(instrument)
      && !matchesType(type, 'caudal', 'flow', 'coriolis', 'másic', 'masic'));
}

// Devuelve la familia metrológica que define la pantalla de calibración.
export function resolveCalibrationFamily(instrument = {}) {
  const type = String(instrument?.type || '').toLowerCase();
  const unit = String(instrument?.unit || '').toLowerCase();

  if (isAnalyticalInstrument(instrument)) return 'analytical';

  if (
    matchesType(type, 'anemómetro', 'anemometro', 'termoanemómetro', 'termoanemometro', 'velocidad de aire', 'luxómetro', 'luxometro', 'iluminancia', 'decibelímetro', 'decibelimetro', 'sonómetro', 'sonometro', 'sound level meter') ||
    ENVIRONMENTAL_UNITS.includes(unit)
  ) {
    return 'environmental';
  }

  if (isBalanceInstrument(instrument) || isMassWeightInstrument(instrument)) {
    return 'mass';
  }

  if (matchesType(type, 'válvula anti retorno', 'valvula anti retorno', 'válvula antirretorno', 'valvula antirretorno', 'check valve', 'non-return valve', 'non return valve')) {
    return 'valve';
  }

  if (matchesType(type, 'válvula de seguridad', 'valvula de seguridad', 'safety valve', 'relief valve', 'psv')) {
    return 'valve';
  }

  if (
    matchesType(
      type,
      'temperat',
      'termometr',
      'termómetr',
      'thermomet',
      'pirómetro',
      'pirometro',
      'pyrometer',
      'rtd',
      'pt100',
      'pt1000',
      'termocupla',
      'thermocouple',
      'estufa',
      'cámara térmica',
      'camara termica',
    ) ||
    TEMPERATURE_UNITS.includes(unit)
  ) {
    return 'temperature';
  }

  if (
    matchesType(
      type,
      'detector de gas', 'detector gas', 'multigas', 'monogas', 'gas detector',
      'h2s', 'sulfuro de hidrógeno', 'co2', 'monóxido de carbono', 'monoxido de carbono',
      'oxígeno', 'oxigeno', 'lel', 'límite inferior de explosividad', 'limite inferior de explosividad',
      'so2', 'nh3', 'amoníaco', 'amoniaco', 'hcn',
    ) || GAS_UNITS.includes(unit)
  ) {
    return 'gas';
  }

  if (
    matchesType(type, 'electric', 'eléctric', 'multim', 'multím', 'multi', 'pinza', 'amper', 'ampér', 'ohm', 'ohmi', 'volt', 'tester', 'megohm', 'meghom', 'megger') ||
    ELECTRICAL_UNITS.includes(unit)
  ) {
    return 'electrical';
  }

  if (
    matchesType(type, 'caudal', 'flowmeter', 'mass flow', 'coriolis', 'rotam', 'flotam', 'totaliz') ||
    FLOW_UNITS.includes(unit)
  ) {
    return 'flow';
  }

  if (
    matchesType(type, 'presost', 'pressure switch', 'manomet', 'manóm', 'vacu', 'presi', 'pressure') ||
    PRESSURE_UNITS.includes(unit)
  ) {
    return 'pressure';
  }

  if (
    matchesType(
      type,
      'force',
      'fuerza',
      'torque',
      'torquímetro',
      'torquimetro',
      'dinamómetro',
      'dinamometro',
      'dynamometer',
      'tensiómetro',
      'tensiometro',
      'célula de carga',
      'celula de carga',
      'load cell',
    ) || FORCE_UNITS.includes(unit)
  ) {
    return 'force';
  }

  if (
    matchesType(
      type,
      'dimensional', 'calibre', 'caliper', 'pie de rey', 'micrómetro', 'micrometro',
      'regla', 'rule', 'flexómetro', 'flexometro', 'profundidad', 'depth gauge',
      'espesor', 'thickness', 'ultrason', 'ultrasón', 'revestimiento', 'coating', 'rugos',
      'comparador', 'soldadura', 'tres contactos', '3 contactos', 'peine', 'rosca',
      'tamiz', 'probeta', 'pieza patrón', 'pieza patron', 'goniómetro', 'goniometro', 'nivel digital',
    ) || DIMENSIONAL_UNITS.includes(unit)
  ) {
    return 'dimensional';
  }

  return null;
}

export function resolveCalibrationRoute(instrument = {}, { serviceOrderCode = '' } = {}) {
  const id = instrument?.id;
  if (!id) return null;
  const family = resolveCalibrationFamily(instrument);
  const query = serviceOrderCode ? `?serviceOrderCode=${encodeURIComponent(serviceOrderCode)}` : '';
  if (family === 'analytical') {
    const type = String(instrument?.type || '').toLowerCase();
    let templateCode = 'ANALYTICAL-PH:ph_meter:v1';
    let path = 'ph-meter';
    if (matchesType(type, 'conductiv')) { templateCode = 'ANALYTICAL-CONDUCTIVITY:conductivity_meter:v1'; path = 'conductivity-meter'; }
    else if (matchesType(type, 'turbid')) { templateCode = 'ANALYTICAL-TURBIDITY:turbidimeter:v1'; path = 'turbidimeter'; }
    else if (matchesType(type, 'oxímetro', 'oximetro', 'oxígeno disuelto', 'oxigeno disuelto')) { templateCode = 'ANALYTICAL-DO:oximeter:v1'; path = 'oximeter'; }
    else if (matchesType(type, 'refract', 'brix')) { templateCode = 'ANALYTICAL-BRIX:refractometer:v1'; path = 'refractometer'; }
    else if (matchesType(type, 'alcohol')) { templateCode = 'ANALYTICAL-ALCOHOL:alcohol_meter:v1'; path = 'alcohol-meter'; }
    else if (matchesType(type, 'mostímetro', 'mostimetro', 'baumé', 'baume')) { templateCode = 'ANALYTICAL-BAUME:must_meter:v1'; path = 'must-meter'; }
    else if (matchesType(type, 'densímetro', 'densimetro')) { templateCode = 'ANALYTICAL-DENSITY:density_meter:v1'; path = 'density-meter'; }
    else if (matchesType(type, 'bureta', 'micropipeta', 'pipeta', 'material volumétrico', 'material volumetrico')) { templateCode = 'ANALYTICAL-VOLUME:volumetric:v1'; path = 'volumetric'; }
    const params = new URLSearchParams();
    params.set('templateCode', templateCode);
    if (serviceOrderCode) params.set('serviceOrderCode', serviceOrderCode);
    return `/ops/calibrations/analytical/${path}/${id}?${params.toString()}`;
  }
  if (family === 'environmental') {
    const type = String(instrument?.type || '').toLowerCase();
    const unit = String(instrument?.unit || '').toLowerCase();
    let templateCode = 'ENV-AIR:anemometer:v1';
    let path = 'anemometer';
    if (matchesType(type, 'lux', 'iluminancia') || ['lx', 'lux', 'klx'].includes(unit)) {
      templateCode = 'ENV-LIGHT:luxmeter:v1'; path = 'luxmeter';
    } else if (matchesType(type, 'decibel', 'sonómetro', 'sonometro', 'sound level') || ['db', 'dba', 'dbc'].includes(unit)) {
      templateCode = 'ENV-SOUND:sound_level_meter:v1'; path = 'sound-level-meter';
    }
    const params = new URLSearchParams();
    params.set('templateCode', templateCode);
    if (serviceOrderCode) params.set('serviceOrderCode', serviceOrderCode);
    return `/ops/calibrations/environmental/${path}/${id}?${params.toString()}`;
  }
  if (family === 'temperature') {
    const type = String(instrument?.type || '').toLowerCase();
    let templateCode = 'TEMP-CONTACT:thermometer:v2';
    if (matchesType(type, 'pirómetro', 'pirometro', 'pyrometer', 'infrarro')) {
      templateCode = 'TEMP-IR:pyrometer:v1';
    } else if (matchesType(type, 'rtd', 'pt100', 'pt1000', 'termocupla', 'thermocouple')) {
      templateCode = 'TEMP-SENSOR:rtd_thermocouple:v1';
    } else if (matchesType(type, 'estufa', 'cámara térmica', 'camara termica', 'cámara climática', 'camara climatica', 'incubadora', 'horno')) {
      templateCode = 'TEMP-CHAMBER:oven_chamber:v1';
    }
    const params = new URLSearchParams();
    params.set('templateCode', templateCode);
    if (serviceOrderCode) params.set('serviceOrderCode', serviceOrderCode);
    const path = templateCode === 'TEMP-IR:pyrometer:v1'
      ? 'infrared'
      : templateCode === 'TEMP-SENSOR:rtd_thermocouple:v1'
        ? 'sensor'
        : templateCode === 'TEMP-CHAMBER:oven_chamber:v1'
          ? 'chamber'
          : 'contact';
    return `/ops/calibrations/temperature/${path}/${id}?${params.toString()}`;
  }
  if (family === 'electrical') return `/ops/calibrations/electrical/multimeter/${id}${query}`;
  if (family === 'pressure') return `/ops/calibrations/pressure/manometer/${id}/template${query}`;
  if (family === 'flow') {
    const type = String(instrument?.type || '').toLowerCase();
    let templateCode = 'FLOW-VOLUME:electromagnetic:v1';
    let path = 'electromagnetic';
    if (isMassFlowInstrument(instrument)) {
      templateCode = 'FLOW-MASS:coriolis:v1';
      path = 'mass';
    } else if (matchesType(type, 'mecánico', 'mecanico', 'totaliz', 'turbina', 'engranaje')) {
      templateCode = 'FLOW-VOLUME:mechanical_totalizer:v1';
      path = 'mechanical-totalizer';
    } else if (matchesType(type, 'ultrasónico', 'ultrasonico', 'ultrasonic', 'clamp-on', 'clamp on')) {
      templateCode = 'FLOW-VOLUME:ultrasonic:v1';
      path = 'ultrasonic';
    } else if (matchesType(type, 'rotam', 'flotam')) {
      templateCode = 'FLOW-VOLUME:rotameter:v1';
      path = 'rotameter';
    }
    const params = new URLSearchParams();
    params.set('templateCode', templateCode);
    if (serviceOrderCode) params.set('serviceOrderCode', serviceOrderCode);
    return `/ops/calibrations/flow/${path}/${id}?${params.toString()}`;
  }
  if (family === 'dimensional') {
    const type = String(instrument?.type || '').toLowerCase();
    let templateCode = 'DIM-LONG:caliper:v1';
    let path = 'caliper';
    if (matchesType(type, 'microm', 'micróm')) { templateCode = 'DIM-LONG:micrometer:v1'; path = 'micrometer'; }
    else if (matchesType(type, 'depth', 'profund')) { templateCode = 'DIM-LONG:depth:v1'; path = 'depth-gauge'; }
    else if (matchesType(type, 'rule', 'regla', 'flex')) { templateCode = 'DIM-LONG:rule:v1'; path = 'rule-flexometer'; }
    else if (matchesType(type, 'ultrason', 'ultrasón') && matchesType(type, 'angular')) { templateCode = 'DIM-THICK:ultrasonic_angular:v1'; path = 'ultrasonic-angular'; }
    else if (matchesType(type, 'ultrason', 'ultrasón', 'medidor de espesores')) { templateCode = 'DIM-THICK:ultrasonic:v1'; path = 'ultrasonic-thickness'; }
    else if (matchesType(type, 'revest', 'coating', 'rugos')) { templateCode = 'DIM-THICK:coating:v1'; path = 'coating-roughness'; }
    else if (matchesType(type, 'soldadura')) {
      if (matchesType(type, 'angular', 'ángulo', 'angulo', 'angle', 'bisel')) { templateCode = 'DIM-ANGLE:welding_gauge:v1'; path = 'welding-gauge-angle'; }
      else { templateCode = 'DIM-SPECIAL:welding_gauge:v1'; path = 'welding-gauge'; }
    }
    else if (matchesType(type, 'comparador')) { templateCode = 'DIM-SPECIAL:comparator:v1'; path = 'comparator'; }
    else if (matchesType(type, 'tres contactos', '3 contactos')) { templateCode = 'DIM-SPECIAL:three_contact:v1'; path = 'three-contact'; }
    else if (matchesType(type, 'peine', 'rosca')) { templateCode = 'DIM-SPECIAL:thread_gauge:v1'; path = 'thread-gauge'; }
    else if (matchesType(type, 'tamiz')) { templateCode = 'DIM-SPECIAL:sieve:v1'; path = 'sieve'; }
    else if (matchesType(type, 'probeta', 'pieza patrón', 'pieza patron')) { templateCode = 'DIM-SPECIAL:reference_piece:v1'; path = 'reference-piece'; }
    else if (matchesType(type, 'goniómetro', 'goniometro', 'nivel digital')) { templateCode = 'DIM-ANGLE:goniometer_level:v1'; path = 'angle'; }
    const params = new URLSearchParams();
    params.set('templateCode', templateCode);
    if (serviceOrderCode) params.set('serviceOrderCode', serviceOrderCode);
    return `/ops/calibrations/dimensional/${path}/${id}?${params.toString()}`;
  }
  if (family === 'mass' && isBalanceInstrument(instrument)) return `/ops/calibrations/mass/balance/${id}${query}`;
  if (family === 'force') return `/ops/calibrations/force/torque/${id}${query}`;
  if (family === 'gas') return `/ops/calibrations/gas/detector/${id}${query}`;
  if (family === 'valve' && matchesType(instrument?.type, 'anti retorno', 'antirretorno', 'check valve', 'non-return valve', 'non return valve')) return `/ops/calibrations/valve/check/${id}${query}`;
  if (family === 'valve') return `/ops/calibrations/valve/safety/${id}${query}`;
  return null;
}

export function calibrationFamilyLabel(family) {
  if (family === 'temperature') return 'Temperatura';
  if (family === 'electrical') return 'Eléctrica';
  if (family === 'pressure') return 'Presión';
  if (family === 'flow') return 'Caudal';
  if (family === 'mass') return 'Masa';
  if (family === 'force') return 'Fuerza / Torque';
  if (family === 'gas') return 'Gases';
  if (family === 'valve') return 'Válvulas';
  if (family === 'dimensional') return 'Dimensional';
  if (family === 'environmental') return 'SSHH / Ambiental';
  if (family === 'analytical') return 'Analítica';
  return 'Sin definir';
}
